/**
 * "Baixar contatos" — a lista de contatos de vários grupos (ou de todos) num arquivo só.
 *
 * O pedido do dono: *"baixar a lista de contatos de todos os grupos de uma vez, com filtro
 * de quem saiu, de quem está no grupo; quando selecionar grupos, poder selecionar todos de
 * uma única vez"*.
 *
 * O que esta tela faz de propósito:
 *
 *  1. **Os números aparecem ANTES do clique**, já com os filtros: linhas, pessoas e
 *     telefones vêm do servidor (invariante 3), pela mesma regra que gera o arquivo.
 *  2. **Linha ≠ pessoa.** Quem está em 3 grupos sai em 3 linhas (uma por grupo, com o nome
 *     dele). Quando os dois números diferem, a tela diz isso em uma frase.
 *  3. **Grupo nunca varrido entra, mas avisado**: sabemos quantos estão lá, não quem — ele
 *     sai quase vazio, e a pessoa precisa saber antes, não no dia em que o arquivo faltar.
 *  4. **O download é um link**, não `fetch`: o navegador salva o arquivo com o nome que o
 *     servidor mandou e a aba nunca segura milhares de contatos na memória.
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  FormAlert,
  Input,
  SkeletonText,
  Tabs,
  TabsList,
  TabsTrigger,
  buttonVariants,
  cn,
  formatNumber,
} from '@mgdlead/ui';
import { Download, ShieldAlert } from 'lucide-react';

import { groupDisplayName, participantsKnown } from '../group/group-ui';
import {
  groupIdsDaSelecao,
  useGruposParaContatos,
  useWorkspaceContactsPreview,
  workspaceContactsExportUrl,
  type ContatosAdmins,
  type ContatosFiltro,
  type ContatosSituacao,
  type WorkspaceContactsPreview,
} from './contatos-export';

export interface BaixarContatosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** Grupos já marcados na lista de grupos. Vazio = começa com todos marcados. */
  initialSelection?: readonly string[];
  /** `can(role, 'group:export_contacts')`. Esconder é UX; quem barra é o servidor. */
  allowed: boolean;
}

const SITUACOES: { value: ContatosSituacao; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'no_grupo', label: 'Estão no grupo' },
  { value: 'saiu', label: 'Saíram' },
];

function plural(n: number, um: string, varios: string): string {
  return `${formatNumber(n)} ${n === 1 ? um : varios}`;
}

export function BaixarContatosDialog(props: BaixarContatosDialogProps) {
  // Montado só com o diálogo aberto: cada abertura recomeça da seleção que veio da lista.
  if (!props.open || !props.allowed) return null;
  return <Conteudo {...props} />;
}

function Conteudo({
  open,
  onOpenChange,
  workspaceId,
  initialSelection = [],
  allowed,
}: BaixarContatosDialogProps) {
  const lista = useGruposParaContatos(workspaceId, allowed);
  const groups = useMemo(() => lista.data?.groups ?? [], [lista.data]);

  /**
   * `null` = a pessoa ainda não mexeu: vale a seleção que veio da lista de grupos (ou
   * todos). Derivada, e não copiada num `useState` inicial, porque os grupos chegam DEPOIS
   * da abertura — e trocar o `<dialog>` de esqueleto por outro faria a tela piscar.
   */
  const [escolha, setEscolha] = useState<ReadonlySet<string> | null>(null);
  const inicial = useMemo(() => {
    const iniciais = initialSelection.filter((id) => groups.some((g) => g.id === id));
    return new Set(iniciais.length > 0 ? iniciais : groups.map((g) => g.id));
  }, [groups, initialSelection]);
  const marcados = escolha ?? inicial;
  const setMarcados = (mudar: (atual: ReadonlySet<string>) => ReadonlySet<string>) =>
    setEscolha((atual) => mudar(atual ?? inicial));
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<ContatosSituacao>('todos');
  const [admins, setAdmins] = useState<ContatosAdmins>('incluir');

  const visiveis = useMemo(() => {
    const termos = busca.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (termos.length === 0) return groups;
    return groups.filter((g) => {
      const alvo = groupDisplayName(g).toLowerCase();
      return termos.every((t) => alvo.includes(t));
    });
  }, [busca, groups]);

  const marcadosVisiveis = visiveis.filter((g) => marcados.has(g.id)).length;
  const totalMarcados = groups.filter((g) => marcados.has(g.id)).length;
  const buscando = busca.trim() !== '';

  const groupIds = groupIdsDaSelecao(marcados, groups);
  const filtro: ContatosFiltro | undefined =
    groupIds === undefined ? undefined : { groupIds, situacao, admins };
  const previa = useWorkspaceContactsPreview(workspaceId, filtro, allowed);
  const dados = filtro === undefined ? undefined : previa.data;

  /** Tri-state sobre o que está À VISTA: com busca, marca/desmarca só os encontrados. */
  const alternarVisiveis = () =>
    setMarcados((atual) => {
      const proximo = new Set(atual);
      const todosMarcados = visiveis.length > 0 && marcadosVisiveis === visiveis.length;
      for (const g of visiveis) {
        if (todosMarcados) proximo.delete(g.id);
        else proximo.add(g.id);
      }
      return proximo;
    });

  const alternar = (id: string) =>
    setMarcados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  const podeBaixar = filtro !== undefined && dados !== undefined && dados.rows > 0;
  const rotuloBaixar =
    dados === undefined ? 'Baixar contatos' : `Baixar ${plural(dados.rows, 'contato', 'contatos')}`;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Baixar contatos"
      description="Escolha os grupos e quem entra no arquivo. Os números abaixo já consideram tudo o que você marcou."
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-2">
          {podeBaixar ? (
            /*
              Link, e não botão com `fetch`: o navegador salva o arquivo com o nome que o
              servidor mandou e a aba não segura a base inteira na memória.
            */
            <a
              href={workspaceContactsExportUrl(workspaceId, filtro)}
              download
              className={cn(buttonVariants({ variant: 'primary', block: true }), 'gap-1.5')}
              onClick={() => onOpenChange(false)}
            >
              <Download aria-hidden="true" className="size-4" />
              {rotuloBaixar}
            </a>
          ) : (
            <Button variant="primary" block leadingIcon={<Download />} disabled>
              {rotuloBaixar}
            </Button>
          )}
          <Button variant="ghost" block onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </div>
      }
    >
      {lista.data === undefined ? (
        lista.isError ? (
          <FormAlert tone="warning">
            Não conseguimos carregar os grupos agora. Tente de novo em instantes.
          </FormAlert>
        ) : (
          <SkeletonText lines={4} />
        )
      ) : (
        <div className="flex flex-col gap-5">
          {/* ------------------------------ grupos ------------------------------ */}
          <section aria-labelledby="baixar-contatos-grupos" className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 id="baixar-contatos-grupos" className="text-sm font-medium">
                Grupos
              </h3>
              <span className="text-xs text-muted-foreground nums" aria-live="polite">
                {formatNumber(totalMarcados)} de {formatNumber(groups.length)}
              </span>
            </div>

            <Input
              search
              size="sm"
              placeholder="Buscar grupo pelo nome"
              aria-label="Buscar grupo pelo nome"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />

            <div className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 max-md:min-h-11">
              <Checkbox
                checked={visiveis.length > 0 && marcadosVisiveis === visiveis.length}
                indeterminate={marcadosVisiveis > 0 && marcadosVisiveis < visiveis.length}
                disabled={visiveis.length === 0}
                onChange={alternarVisiveis}
                label={
                  buscando
                    ? `Selecionar os ${formatNumber(visiveis.length)} encontrados`
                    : 'Selecionar todos'
                }
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={totalMarcados === 0}
                onClick={() => setMarcados(() => new Set())}
              >
                Limpar
              </Button>
            </div>

            <ul
              className="flex max-h-60 flex-col divide-y divide-border overflow-y-auto rounded-md border border-border scrollbar-thin"
              aria-label="Grupos para baixar"
            >
              {visiveis.length === 0 ? (
                <li className="px-3 py-3 text-sm text-muted-foreground">
                  Nenhum grupo com “{busca.trim()}”.
                </li>
              ) : (
                visiveis.map((g) => (
                  <li
                    key={g.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 max-md:min-h-11"
                  >
                    <Checkbox
                      checked={marcados.has(g.id)}
                      onChange={() => alternar(g.id)}
                      label={groupDisplayName(g)}
                    />
                    <span className="flex shrink-0 gap-1">
                      {g.archivedAt !== null ? <Badge tone="neutral">Não monitorado</Badge> : null}
                      {!participantsKnown(g) ? (
                        <Badge
                          tone="warning"
                          dot
                          title="Sabemos quantas pessoas estão lá, não quem são."
                        >
                          Sem varredura
                        </Badge>
                      ) : null}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </section>

          {/* ---------------------------- quem entra ---------------------------- */}
          <section aria-labelledby="baixar-contatos-quem" className="flex flex-col gap-2">
            <h3 id="baixar-contatos-quem" className="text-sm font-medium">
              Situação
            </h3>
            <div className="max-w-full overflow-x-auto scrollbar-thin">
              <Tabs
                variant="segmented"
                value={situacao}
                onValueChange={(valor: string) => setSituacao(valor as ContatosSituacao)}
              >
                <TabsList label="Quem entra no arquivo">
                  {SITUACOES.map((s) => (
                    <TabsTrigger key={s.value} value={s.value}>
                      {s.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <Checkbox
              checked={admins === 'excluir'}
              onChange={() => setAdmins((atual) => (atual === 'excluir' ? 'incluir' : 'excluir'))}
              label="Sem administradores"
              hint="Tira do arquivo quem é admin do grupo — normalmente a sua própria equipe."
            />
          </section>

          {/* ------------------------------ prévia ------------------------------ */}
          <section
            aria-live="polite"
            aria-label="O que vai sair no arquivo"
            className="flex flex-col gap-2"
          >
            {filtro === undefined ? (
              <FormAlert tone="info">
                Marque pelo menos um grupo para ver o que sai no arquivo.
              </FormAlert>
            ) : previa.isError ? (
              <FormAlert tone="warning">
                Não conseguimos conferir o que sairia no arquivo agora. Tente de novo em instantes.
              </FormAlert>
            ) : dados === undefined ? (
              <SkeletonText lines={2} />
            ) : (
              <Previa dados={dados} atualizando={previa.isFetching} />
            )}
          </section>

          <p className="text-2xs text-muted-foreground">
            <ShieldAlert
              aria-hidden="true"
              className="mr-1 inline size-3.5 align-[-0.125rem] text-warning"
            />
            O arquivo leva telefone em claro. A partir do download ele é um dado pessoal sob a sua
            guarda, e a exportação fica registrada: quem baixou, quando, quantas linhas e com quais
            filtros.
          </p>
        </div>
      )}
    </Dialog>
  );
}

function Previa({ dados, atualizando }: { dados: WorkspaceContactsPreview; atualizando: boolean }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border p-3',
        atualizando && 'opacity-70',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-2xl font-semibold nums">{formatNumber(dados.rows)}</span>
        <span className="text-sm text-muted-foreground">
          {dados.rows === 1 ? 'linha no arquivo' : 'linhas no arquivo'}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        <Badge tone="outline" numeric>
          {plural(dados.people, 'pessoa diferente', 'pessoas diferentes')}
        </Badge>
        <Badge
          tone={dados.rowsWithPhone === dados.rows ? 'success' : 'warning'}
          dot
          numeric
          title="Sem telefone não dá para chamar a pessoa de volta."
        >
          {formatNumber(dados.rowsWithPhone)} com telefone
        </Badge>
        <Badge tone="outline" numeric>
          {plural(dados.groups, 'grupo', 'grupos')}
        </Badge>
      </div>

      {dados.rows > dados.people ? (
        <p className="text-xs text-muted-foreground">
          Quem está em mais de um grupo aparece uma vez por grupo, com o nome do grupo na linha —
          por isso {plural(dados.rows, 'linha', 'linhas')} para{' '}
          {plural(dados.people, 'pessoa', 'pessoas')}.
        </p>
      ) : null}

      {dados.neverSyncedGroups > 0 ? (
        <FormAlert tone="warning">
          {dados.neverSyncedGroups === 1
            ? '1 grupo escolhido ainda não teve as pessoas sincronizadas'
            : `${formatNumber(dados.neverSyncedGroups)} grupos escolhidos ainda não tiveram as pessoas sincronizadas`}
          : sabemos quantas pessoas estão lá, não quem são, e eles saem quase vazios. Sincronize as
          pessoas na lista de grupos antes de baixar, se quiser o arquivo completo.
        </FormAlert>
      ) : null}

      {dados.rows === 0 ? (
        <p className="text-xs text-muted-foreground">
          Ninguém com esses filtros — não há o que baixar.
        </p>
      ) : null}
    </div>
  );
}
